import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Badge } from '@shared/components/ui/shadcn/badge';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { Clock, Package, User } from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useLocationOrders } from '../../hooks/useLocationOrders';

export interface LocationOrdersModalRef {
  open: (receptionId: number) => void;
  close: () => void;
}

const STATUS_LABELS: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { label: 'Pendiente', variant: 'secondary' },
  in_progress: { label: 'En Progreso', variant: 'default' },
  done: { label: 'Completada', variant: 'default' },
  cancelled: { label: 'Cancelada', variant: 'destructive' },
};

export const LocationOrdersModal = forwardRef<LocationOrdersModalRef>((_, ref) => {
  const modalRef = useRef<ModalCenterRef>(null);
  const [receptionId, setReceptionId] = useState<number | null>(null);

  const { data, isLoading } = useLocationOrders(receptionId || 0, { per_page: 100 }, !!receptionId);

  useImperativeHandle(ref, () => ({
    open: (id: number) => {
      setReceptionId(id);
      modalRef.current?.openModal();
    },
    close: () => {
      modalRef.current?.closeModal();
      setReceptionId(null);
    },
  }));

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    const isoDateString = dateString.replace(' ', 'T');
    const date = new Date(isoDateString);
    if (isNaN(date.getTime())) return dateString;

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const orders = data?.data || [];

  return (
    <ModalCenter ref={modalRef} title="Órdenes de Ubicación" type="info" width="xxl">
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No hay órdenes de ubicación creadas</p>
          </div>
        ) : (
          <>
            {/* Encabezados */}
            <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-muted/70 rounded-lg text-sm text-foreground">
              <div className="col-span-1">ID</div>
              <div className="col-span-2">Carrito</div>
              <div className="col-span-2">Productos</div>
              <div className="col-span-2">Cantidad</div>
              <div className="col-span-4">Creado por / Fecha</div>
              <div className="col-span-1 text-center">Estado</div>
            </div>

            {/* Lista */}
            <ScrollArea className="h-[500px]">
              <div className="space-y-2 pr-4">
                {orders.map((order) => {
                  const statusConfig = STATUS_LABELS[order.status] || STATUS_LABELS.pending;
                  const totalProducts = order.total_products || order.lines.length;
                  const totalQuantity =
                    order.total_quantity || order.lines.reduce((sum, line) => sum + line.qty_planned, 0);

                  return (
                    <div
                      key={order.id}
                      className="grid grid-cols-12 gap-3 px-4 py-4 border border-border rounded-lg hover:border-primary hover:bg-muted/30 transition-colors bg-background"
                    >
                      {/* ID */}
                      <div className="col-span-1 flex items-center">
                        <span className="text-sm font-medium text-foreground">#{order.id}</span>
                      </div>

                      {/* Carrito */}
                      <div className="col-span-2 flex items-center pl-3">
                        <span className="text-sm font-medium text-foreground">
                          {order.cart?.code || `#${order.cart_id}`}
                        </span>
                      </div>

                      {/* Total Productos */}
                      <div className="col-span-2 flex items-center pl-3">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="text-sm text-foreground">
                            {totalProducts} producto{totalProducts !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>

                      {/* Cantidad Total */}
                      <div className="col-span-2 flex items-center pl-3">
                        <span className="text-sm font-medium text-foreground">{totalQuantity} unidades</span>
                      </div>

                      {/* Creado por / Fecha */}
                      <div className="col-span-4 flex flex-col justify-center pl-3">
                        {order.created_by_user ? (
                          <div className="flex items-center gap-2 mb-1">
                            <User className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm text-foreground truncate">{order.created_by_user.name}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm mb-1">-</span>
                        )}
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-xs text-muted-foreground">{formatDate(order.created_at)}</span>
                        </div>
                      </div>

                      {/* Estado */}
                      <div className="col-span-1 flex items-center justify-center">
                        <Badge variant={statusConfig.variant} className="text-xs">
                          {statusConfig.label}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </>
        )}
      </div>
    </ModalCenter>
  );
});

LocationOrdersModal.displayName = 'LocationOrdersModal';
